import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchDataQuery } from 'api/patchQuery';
import { CardHeader } from 'components/common';
import { PaginationCustom } from 'components/common/pagination';
import UserCreatePopup from 'components/popup/UserCreatePopup';
import BreadCrumb from 'components/shared/BreadCrumb';
import { useAdminUserSetting } from 'hooks/admin/useAdminUserSetting';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { adminQueryKeyState } from 'recoil/adminpopup/adminQueryKeyState';
import { accountTableIndexCalculator, checkUserRole, userNameSplit } from 'util/admin/adminAccountTable';
import { utils } from 'xlsx-js-style';
import { applyExcelStyles, writeExcelFile } from 'util/excelStyle';
import { downloadLoadingState } from 'recoil/loading/downloadLoadingState';

export function UserAccounts() {
  const [queryKeyState, setQueryKeyState] = useRecoilState(adminQueryKeyState);
  const resetQueryKetState = useResetRecoilState(adminQueryKeyState);

  const handleUpdateRole = (e) => {
    setQueryKeyState({ ...queryKeyState, username: search, role: e.target.value, currentPage: 1 });
  };
  const handleUpdateCurrentPage = (currentPage) => {
    setQueryKeyState({ ...queryKeyState, currentPage: currentPage });
  };
  const handleUpdateUsername = () => {
    setQueryKeyState({ ...queryKeyState, username: search, currentPage: 1 });
  };
  const [search, setSearch] = useState('');
  const [visibleCreatePopup, setVisibleCreatePopup] = useState(false);

  const {
    // 전체 유저수
    adminAlluserLengthData,
    adminAlluserLengthIsLoading,
    adminAlluserLengthIsError,

    // 전체 유저 데이터 (페이징 + 엑셀 다운로드용)
    adminUserAllData,
    adminUserAllIsLoading,
    adminUserAllIsError,
  } = useAdminUserSetting();

  const queryClient = useQueryClient();
  const deleteUserMutation = useMutation({
    mutationFn: (userNumber) => patchDataQuery(`/users?id=${userNumber}`),
    onSuccess: () => {
      alert('사용자가 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['adminUserAll'] });
      queryClient.invalidateQueries({ queryKey: ['adminUserProfile'] });
    },
    onError: (error) => {
      console.error(error);
      alert('삭제에 실패했습니다.');
    },
  });

  const handleDeleteUser = (userNumber, username) => {
    if (window.confirm(`${username} 사용자를 삭제하시겠습니까?`)) {
      deleteUserMutation.mutate(userNumber);
    }
  };

  const itemsPerPage = 10;

  // 전체 데이터에서 필터링
  const filteredData = useMemo(() => {
    if (!adminUserAllData) return [];

    return adminUserAllData.filter((item) => {
      const matchRole = !queryKeyState.role || item.role === queryKeyState.role;
      const matchName = !queryKeyState.username || item.username?.includes(queryKeyState.username) || item.user_number?.includes(queryKeyState.username);
      return matchRole && matchName;
    });
  }, [adminUserAllData, queryKeyState.role, queryKeyState.username]);

  // 현재 페이지에 해당하는 데이터만 슬라이싱
  const pagedData = useMemo(() => {
    const startIndex = (queryKeyState.currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, queryKeyState.currentPage]);

  const handleSearchBar = (e) => {
    e.preventDefault();
    handleUpdateUsername(search);
  };
  const getTimestamp = () => moment().format('YYMMDD_HHmm');
  const setDownloadLoading = useSetRecoilState(downloadLoadingState);
  const exportFile = useCallback(async () => {
    setDownloadLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      // userData를 엑셀시트 형식으로 변환
      const ws = utils.json_to_sheet(adminUserAllData);
      applyExcelStyles(ws, 1);
      // 빈 엑셀파일을 생성
      const wb = utils.book_new();
      // 엑셀파일에 시트 추가
      utils.book_append_sheet(wb, ws, 'Data');
      // 만들어진 엑셀 파일을 다운로드
      writeExcelFile(wb, `사용자현황_${getTimestamp()}.xlsx`);
    } finally {
      setDownloadLoading(false);
    }
  }, [adminUserAllData]);

  useEffect(() => {
    resetQueryKetState();
    return () => {
      resetQueryKetState();
    };
  }, []);

  return (
    <main id='main' className='main'>
      <BreadCrumb home={'/useraccounts'} sublink={'/useraccounts'} title={'계정 관리 (User Accounts)'} subTitle={'계정관리'}>
        &nbsp; / 사용자 계정관리
      </BreadCrumb>
      <section className='section dashboard row'>
        <div className='col-6'>
          <div className='col'>
            <div className='card card-min info-card sales-card p-20'>
              <div className=''>
                <CardHeader title='전체 사용자'>계정 수</CardHeader>

                <div className='d-flex align-items-center'>
                  <div
                    className='card-icon rounded-circle d-flex align-items-center justify-content-center'
                    style={{ background: '#FFF9EA' }}
                  >
                    <i className='bi bi-people-fill' style={{ color: '#F5CE3E' }}></i>
                  </div>
                  <div className='me-2 w-100 text-end d-flex justify-content-end align-items-center'>
                    <h6>{adminAlluserLengthData ? adminAlluserLengthData : 0}</h6> <h6 className='ms-3'>개</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <h3 className='fw-bold fz-20 mb-3'>사용자 현황</h3>
        <div className='col'>
          {/* 아래 카드 */}
          <div className='card p-20'>
            {/* header */}
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div className='w-200'>
                <Form.Select
                  className='ms-2 white-nowrap py-0'
                  id='subjectNotice'
                  name='subjectNotice'
                  onChange={handleUpdateRole}
                  value={queryKeyState.role}
                  style={{ height: '31px' }}
                >
                  <option value=''>모든 권한</option>
                  <option value='STUDENT'>학생</option>
                  <option value='TEACHER'>교수</option>
                  <option value='ADMIN'>관리자</option>
                </Form.Select>
              </div>

              <div className='d-flex align-items-center'>
                <form
                  onSubmit={handleSearchBar}
                  className='search-form d-flex align-items-center me-2'
                  style={{ width: '80%' }}
                >
                  <input
                    type='text'
                    className='search-input w-100'
                    name='query'
                    placeholder='이름 또는 학번/사번으로 검색'
                    title='Enter search keyword'
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    style={{ height: '31px' }}
                  />
                  <button>
                    <i className='bi bi-search'></i>
                  </button>
                </form>

                <button onClick={exportFile} className='badge link-badge border fz-14 me-2'>
                  <i className='bi bi-download'></i>
                </button>
                <button onClick={() => setVisibleCreatePopup(true)} className='badge link-badge border fz-14 me-2'>
                  <i className='bi bi-plus-lg'></i> 계정 추가
                </button>
              </div>
            </div>
            {/* body */}
            <div>
              <Table>
                <thead>
                  <tr className='fw-bold border-bottom'>
                    <th className='py-20 text-center'>번호</th>
                    <th className='py-20 text-start'>학번 /사번 (로그인ID)</th>
                    <th className='py-20 text-start'>이름</th>
                    <th className='py-20 text-start'>학부 /학과</th>
                    <th className='py-20 text-start'>학습보드 권한</th>
                    <th className='py-20 text-start'>최근접속일시</th>
                    <th className='py-20 text-center' colSpan={2}>
                      관리
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adminUserAllIsLoading ? (
                    <tr>
                      <td colSpan={7} className='text-center py-5'>
                        <div className='spinner-border spinner-border-sm me-2' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        데이터를 불러오는 중입니다...
                      </td>
                    </tr>
                  ) : (
                    pagedData?.map((item, index) => (
                      <tr key={item.user_number} className='border-bottom'>
                        <td className='py-20 text-center' style={{ width: '7.07%' }}>
                          {accountTableIndexCalculator(index, queryKeyState.currentPage)}
                        </td>
                        <td className='py-20 text-start' style={{ width: '18.57%' }}>
                          {item.user_number}
                        </td>
                        <td className='py-20 text-start' style={{ width: '12.87%' }}>
                          {userNameSplit(item.username)}
                        </td>
                        <td className='py-20 text-start' style={{ width: '18.57%' }}>
                          {item.division}
                          {item.sub_division}
                        </td>
                        <td className='py-20 text-start' style={{ width: '11.79%' }}>
                          {checkUserRole(item.role)}
                        </td>
                        <td className='py-20 text-start'>
                          {item.login_hist ? moment(item.login_hist).format('YYYY-MM-DD HH:mm:ss') : '-'}
                        </td>
                        <td className='py-20 text-start'>
                          <div className='d-flex justify-content-center'>
                            <Link
                              to={`/useraccounts/setting/${item.user_number}`}
                              className='badge link-badge border fz-14 bg-second'
                            >
                              자세히 보기
                            </Link>
                          </div>
                        </td>
                        <td className='py-20 text-start'>
                          <div className='d-flex justify-content-center'>
                            <button
                              className='badge link-badge link-badge-danger border fz-14'
                              onClick={() => handleDeleteUser(item.user_number, userNameSplit(item.username))}
                            >
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {/* footer */}
              <div>
                {filteredData.length > 0 && (
                  <div className='d-flex align-items-center justify-content-center position-relative'>
                    <p className='float-left position-absolute top-left-50 fz-12'>
                      전체 게시물수 : {filteredData.length} 전체 페이지 : {queryKeyState.currentPage} /
                      {Math.ceil(filteredData.length / itemsPerPage)}
                    </p>
                    <PaginationCustom
                      currentPage={queryKeyState.currentPage}
                      setCurrentPage={handleUpdateCurrentPage}
                      importantDataLength={filteredData.length}
                      itemsCountPerPage={itemsPerPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {visibleCreatePopup && (
        <UserCreatePopup setVisiblePopup={setVisibleCreatePopup} />
      )}
    </main>
  );
}
